import { useState, useEffect } from 'react';
import _ from 'lodash';

import getLoginToken from 'api/login-token';

import { ItemType } from 'components/atoms/list-item';

import lurkerMetrics from 'util/lurker-metrics';
import { gtmTriggerEvent } from 'lib/gtm';
import { MY_REQUESTS_URL } from 'consts';
import casebyEmail from 'api/get-case-email';
import { ResponseQuerySalesforceData } from 'pages/api/v1/utils/crm-sf-olx';
import { addRecordingTag } from 'util/config-mouse-flow';
import {
  CatalagProps,
  ItemProps,
  ServiceProps,
} from 'components/organisms/list/types';
import { getIsCampana } from 'util/getIsCampana';
import { shouldGoToChat } from 'util/guest-chat-rollout';
import * as r from '../../routes';

const { Router } = r;

const SERVICE_TYPE = 'Service';
const AMPERSAND_CODE = '%26';

type Props = {
  loggedInUserEmail?: string;
  onClick: Function;
  // eslint-disable-next-line react/require-default-props
  stepToShow?: number;
  // eslint-disable-next-line react/require-default-props
  onChangeCatalog?: Function;
  catalogs: CatalagProps[];
  services: ServiceProps[];
  loggedUser: any;
};

const getCurrentCatalog = (catalogs: any, currentState: any) => {
  return _.find(catalogs, (catalog) => catalog.id === currentState);
};

const getService = (services: any) => {
  return _.map(services, (service) => {
    return {
      type: ItemType.link,
      ...service,
    };
  });
};

const getCatalogsToShow = (
  currentCatalog: any,
  catalogs: any,
  selectedId: any,
  services: any,
) => {
  if (currentCatalog && selectedId) {
    return _.concat(
      [],
      currentCatalog.catalogs,
      getService(currentCatalog.services),
    );
  }

  const itens = _.concat(
    _.filter(catalogs, (catalog) => !catalog.parent?.id),
    getService(services),
  );
  return _.sortBy(itens, (i) => i.position);
};

export const useContactUsLit = (props: Props) => {
  const {
    loggedUser,
    loggedInUserEmail,
    catalogs,
    services,
    onClick,
    stepToShow = 0,
    onChangeCatalog,
  } = props;
  const [loading, setLoading] = useState(false);
  const [catalogId, setCatalogId] = useState<string | undefined>();
  const [isLoginModalVisible, setLoginModalVisible] = useState(false);
  const [routes, setRoutes] = useState<string[]>([]);

  const currentCatalog = getCurrentCatalog(catalogs, catalogId);
  const onLoginConfirmClick = async () => {
    try {
      lurkerMetrics('click', 'login_confirm', {
        object_name: 'order_list',
      });
      const { token } = await getLoginToken('mediation');
      (
        window as any
      ).location = `https://conta.olx.com.br/acesso?returnToToken=${token}`;
    } catch (e) {
      lurkerMetrics('error', 'login_error', {
        object_name: 'order_list',
      });
    }
  };

  const handleMyPurchases = () => {
    if (!loggedUser) {
      setLoginModalVisible(true);
    } else {
      (window as any).location = `${MY_REQUESTS_URL}/compras`;
    }
  };

  const redirectToExternalLink = (link: string) => {
    (window as any).location = link;
  };

  const handleLinkClick = (item: ItemProps) => {
    onClick(item, routes);

    if (item.selfservice?.slug) {
      Router.pushRoute(`resolva-mais-rapido/${item.selfservice.slug}`);
    } else {
      const nextRoute = shouldGoToChat(item.contact_by_chat, loggedUser)
        ? 'contact-options-bot'
        : 'contact-form';

      Router.pushRoute(nextRoute, {
        assunto: item.slug,
      });
    }
  };

  const handleDeactivateLoading = () => {
    setTimeout(() => {
      setLoading(false);
    }, 2500);
  };

  // eslint-disable-next-line consistent-return
  const handleClickFalseCase = async (item: ItemProps, org: string) => {
    setLoading(true);

    try {
      const caseResponse: ResponseQuerySalesforceData = await casebyEmail(
        loggedInUserEmail,
        org,
      );

      const hasCase = caseResponse?.records?.find((record) =>
        record?.OrigemZendesk__c?.endsWith(item.external_id),
      );

      if (hasCase) {
        const allRoutes = [...routes, item.name];
        const routesInString = allRoutes
          .map((routePart) => encodeURIComponent(routePart))
          .join(AMPERSAND_CODE);

        return routesInString;
      }
    } catch (error) {
      console.error('Error on get history cases', error);
    }
  };

  const sendMetricServiceToLurker = (item: ItemProps) => {
    lurkerMetrics('click', 'service_click_catalog', {
      object_name: 'contact_us',
      data: {
        type: item.__typename,
        name: item.name,
        org: item.data_source,
        contact_by_chat: item.contact_by_chat,
        route: routes?.join(' > '),
      },
    });
  };

  const handleClick = async (item: ItemProps) => {
    const isService = item.__typename === SERVICE_TYPE;

    sendMetricServiceToLurker(item);
    if (isService) addRecordingTag(item.slug);

    setRoutes([...routes, item.name]);
    if (isService && item.incident?.publishedAt) {
      const nextRouteIncident = `/incidente/${item.incident.id}`;
      Router.pushRoute(nextRouteIncident);

      return;
    }

    if (isService && loggedInUserEmail) {
      const isCampana = getIsCampana(item.data_source);
      const org = isCampana ? 'campana' : item.data_source;
      const routesInString: string | undefined = await handleClickFalseCase(
        item,
        org,
      );

      if (routesInString) {
        Router.pushRoute(
          `chamado/${routesInString}?query=${item.external_id}&slug=${item.slug}&org=${org}`,
        );

        return;
      }
      handleDeactivateLoading();
    }

    if (item.slug === 'minhas-compras') {
      onClick(item, routes);
      handleMyPurchases();
      return;
    }

    if (item.external_link) {
      redirectToExternalLink(item.external_link);
      return;
    }

    if (item.type === ItemType.link) {
      handleLinkClick(item);
    } else {
      setCatalogId(item.id);
    }
  };

  const backHandle = () => {
    gtmTriggerEvent('click_contact_us_catalog_back_button', {
      name: currentCatalog?.name,
    });

    routes.pop();
    setRoutes([...routes]);

    setCatalogId(currentCatalog?.parent?.id);
  };

  useEffect(() => {
    if (currentCatalog) {
      backHandle();
    }
  }, [stepToShow]);

  useEffect(() => {
    if (onChangeCatalog) {
      onChangeCatalog(catalogId);
    }
  }, [catalogId]);

  const catalogsToShow = getCatalogsToShow(
    currentCatalog,
    catalogs,
    catalogId,
    services,
  );

  return {
    loading,
    catalogsToShow,
    handleClick,
    isLoginModalVisible,
    setLoginModalVisible,
    onLoginConfirmClick,
  };
};
